import { useEffect, useState } from 'react';
import { CpmButton, CpmFilterDropdown } from '../../../components/design-system';
import { fetchReportFilterOptions } from '../../../services/reportsAdminService';
import type { CpmState } from '../../../types/centralProductMaster.types';
import {
    ACTIVE_STATUSES,
    REPORT_KINDS,
    type ActiveStatus,
    type ReportKind,
} from '../../../types/reportsAdmin.types';
import { REPORTS_COPY, REPORTS_TIPS } from '../copy';
import AppliedFilterChips, { type AppliedChip } from '../components/AppliedFilterChips';
import FilterToggleChip from '../components/FilterToggleChip';
import InfoI from '../components/InfoI';
import SearchField from '../components/SearchField';

export interface CatalogFilterState {
    search: string;
    states: string[];
    categories: string[];
    kinds: ReportKind[];
    activeStatus: ActiveStatus[];
    enabledNowhere: boolean;
    noGenerator: boolean;
}

interface CatalogFiltersProps {
    value: CatalogFilterState;
    onChange: (next: CatalogFilterState) => void;
    states: CpmState[];
    statesLoading: boolean;
    statesError: boolean;
    onRetryStates: () => void;
    disabled?: boolean;
    onNewReport: () => void;
}

const KIND_LABEL: Record<ReportKind, string> = {
    GENERATED: REPORTS_COPY.KIND_GENERATED,
    ALWAYS_ON: REPORTS_COPY.KIND_ALWAYS_ON,
    DEAD: REPORTS_COPY.KIND_DEAD,
};

export const EMPTY_CATALOG_FILTERS: CatalogFilterState = {
    search: '',
    states: [],
    categories: [],
    kinds: [],
    activeStatus: [],
    enabledNowhere: false,
    noGenerator: false,
};

export default function CatalogFilters({
    value,
    onChange,
    states,
    statesLoading,
    statesError,
    onRetryStates,
    disabled = false,
    onNewReport,
}: CatalogFiltersProps) {
    const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
    const [kindOptions, setKindOptions] = useState<ReportKind[]>([]);
    const [optionsError, setOptionsError] = useState(false);
    const [optionsLoading, setOptionsLoading] = useState(true);

    const loadOptions = async () => {
        setOptionsLoading(true);
        setOptionsError(false);
        try {
            const data = await fetchReportFilterOptions();
            setCategoryOptions(data.categories ?? []);
            setKindOptions((data.kinds ?? []) as ReportKind[]);
        } catch {
            setOptionsError(true);
        } finally {
            setOptionsLoading(false);
        }
    };

    useEffect(() => {
        void loadOptions();
    }, []);

    const stateOptions = states
        .map((state) => state.name || state.state || '')
        .filter(Boolean)
        .map((name) => ({ value: name, label: name }));

    const chips: AppliedChip[] = [];
    value.states.forEach((state) =>
        chips.push({
            key: `state-${state}`,
            label: state,
            onClear: () =>
                onChange({ ...value, states: value.states.filter((item) => item !== state) }),
        })
    );
    value.categories.forEach((category) =>
        chips.push({
            key: `cat-${category}`,
            label: category,
            onClear: () =>
                onChange({
                    ...value,
                    categories: value.categories.filter((item) => item !== category),
                }),
        })
    );
    value.kinds.forEach((kind) =>
        chips.push({
            key: `kind-${kind}`,
            label: KIND_LABEL[kind],
            onClear: () =>
                onChange({ ...value, kinds: value.kinds.filter((item) => item !== kind) }),
        })
    );
    value.activeStatus.forEach((status) =>
        chips.push({
            key: `status-${status}`,
            label: status,
            onClear: () =>
                onChange({
                    ...value,
                    activeStatus: value.activeStatus.filter((item) => item !== status),
                }),
        })
    );
    if (value.enabledNowhere) {
        chips.push({
            key: 'nowhere',
            label: 'On for 0 store brands',
            onClear: () => onChange({ ...value, enabledNowhere: false }),
        });
    }
    if (value.noGenerator) {
        chips.push({
            key: 'nogen',
            label: REPORTS_COPY.CANNOT_BUILD,
            onClear: () => onChange({ ...value, noGenerator: false }),
        });
    }

    return (
        <div>
            <div className="flex flex-wrap items-center" style={{ gap: 9 }}>
                <SearchField
                    value={value.search}
                    onChange={(search) => onChange({ ...value, search })}
                    placeholder="Search report name or description"
                    width={330}
                    size="sm"
                />
                <CpmFilterDropdown
                    label="State"
                    triggerVariant="chip"
                    portal
                    disabled={disabled}
                    menuMinWidth={286}
                    loading={statesLoading}
                    loadingLabel={REPORTS_COPY.LOADING_STATES}
                    failed={statesError}
                    failedTitle={REPORTS_COPY.LOAD_FAIL_STATES}
                    failedBody={REPORTS_COPY.OPTIONS_FAIL_BODY}
                    onRetry={onRetryStates}
                    options={stateOptions}
                    selected={value.states}
                    onChange={(states) => onChange({ ...value, states })}
                />
                <CpmFilterDropdown
                    label="Category"
                    triggerVariant="chip"
                    portal
                    disabled={disabled}
                    menuMinWidth={286}
                    loading={optionsLoading}
                    loadingLabel={REPORTS_COPY.LOADING_CATEGORIES}
                    failed={optionsError}
                    failedTitle={REPORTS_COPY.LOAD_FAIL_CATEGORIES}
                    failedBody={REPORTS_COPY.OPTIONS_FAIL_BODY}
                    onRetry={() => void loadOptions()}
                    options={categoryOptions.map((category) => ({
                        value: category,
                        label: category,
                    }))}
                    selected={value.categories}
                    onChange={(categories) => onChange({ ...value, categories })}
                />
                <CpmFilterDropdown
                    label="Kind"
                    triggerVariant="chip"
                    portal
                    disabled={disabled}
                    options={(kindOptions.length ? kindOptions : [...REPORT_KINDS]).map((kind) => ({
                        value: kind,
                        label: KIND_LABEL[kind] ?? kind,
                    }))}
                    selected={value.kinds}
                    onChange={(kinds) => onChange({ ...value, kinds: kinds as ReportKind[] })}
                />
                <CpmFilterDropdown
                    label="Status"
                    triggerVariant="chip"
                    portal
                    disabled={disabled}
                    options={ACTIVE_STATUSES.map((status) => ({ value: status, label: status }))}
                    selected={value.activeStatus}
                    onChange={(activeStatus) =>
                        onChange({ ...value, activeStatus: activeStatus as ActiveStatus[] })
                    }
                />
                <FilterToggleChip
                    label="On for 0 store brands"
                    on={value.enabledNowhere}
                    disabled={disabled}
                    onClick={() => onChange({ ...value, enabledNowhere: !value.enabledNowhere })}
                />
                <FilterToggleChip
                    label={REPORTS_COPY.CANNOT_BUILD}
                    on={value.noGenerator}
                    disabled={disabled}
                    onClick={() => onChange({ ...value, noGenerator: !value.noGenerator })}
                />
                <div className="flex items-center" style={{ gap: 10, marginLeft: 'auto' }}>
                    <InfoI
                        title={REPORTS_TIPS.CATALOGUE.title}
                        body={REPORTS_TIPS.CATALOGUE.body}
                    />
                    <CpmButton
                        variant="outline"
                        size="sm"
                        disabled={disabled}
                        onClick={onNewReport}
                    >
                        {REPORTS_COPY.NEW_REPORT}
                    </CpmButton>
                </div>
            </div>
            <AppliedFilterChips
                chips={chips}
                onClearAll={() => onChange({ ...EMPTY_CATALOG_FILTERS, search: value.search })}
            />
        </div>
    );
}
